import React, { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ToolExecutionPanel } from '../../components/Pleri/components/ToolExecution/ToolExecutionPanel';
import { FocusedViewPanel } from '../../components/Pleri/components/FocusedView';
import { ChatArea } from '../../components/Pleri/components/ChatArea/ChatArea';
import { useChatStore } from '../../components/Pleri/store/chatStore';
import {
  useMarkFollowUpAsRead,
  useListConversations,
} from '../../api/conversations';
import { useLayoutStore } from '../../store/layoutStore';
import { useSpotlightStore } from '../../components/Pleri/store/spotlightStore';
import { LoadingSpinner } from '../../components/ui/loading-spinner';

import { useTranslation } from 'react-i18next';

// Define search params schema
type ConversationSearchParams = {
  spotlightSummaryId?: string;
};

export const Route = createFileRoute(`/c/$conversationId`)({
  component: RouteComponent,
  validateSearch: (
    search: Record<string, unknown>,
  ): ConversationSearchParams => {
    return {
      spotlightSummaryId:
        typeof search.spotlightSummaryId === 'string'
          ? search.spotlightSummaryId
          : undefined,
    };
  },
  loader: async ({ params }) => {
    const { conversationId } = params;

    return {
      conversationId,
    };
  },
});

function RouteComponent() {
  const { t } = useTranslation('pleri/pages');
  const { conversationId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { mutate: markFollowUpAsRead } = useMarkFollowUpAsRead();
  const { data: conversationsData } = useListConversations();

  const { isMiniMode } = useLayoutStore();
  const { getSpotlightById } = useSpotlightStore();

  // Get spotlightSummaryId from router search params
  const spotlightSummaryId = search.spotlightSummaryId;
  const [isLoadingSpotlight, setIsLoadingSpotlight] =
    useState(!!spotlightSummaryId);

  // Check if focused view is open
  const isFocusedViewOpen = useChatStore((state) => {
    const activeConversation = state.activeConversationId
      ? state.conversations[state.activeConversationId]
      : null;
    return activeConversation?.focusedViewOpen || false;
  });

  useEffect(() => {
    // Handle 'new' conversation case
    if (conversationId === 'new') {
      const newConversationId = useChatStore.getState().createConversation();

      // Redirect to the new conversation URL
      navigate({
        to: '/c/$conversationId',
        params: { conversationId: newConversationId },
        search: spotlightSummaryId ? { spotlightSummaryId } : undefined,
      });
    } else if (conversationId === 'active') {
      const activeConversationId = useChatStore.getState().activeConversationId;

      // Redirect to the new conversation URL
      navigate({
        to: '/c/$conversationId',
        params: { conversationId: activeConversationId || 'new' },
      });
    } else {
      useChatStore.getState().setActiveConversation({ conversationId });
      // Mark follow-up as read when conversation is opened (only if followUpNotify is true)
      const conversation = conversationsData?.conversations?.find(
        (conv) => conv.conversationId === conversationId,
      );
      if (conversation?.followUpNotify) {
        markFollowUpAsRead(conversationId);
      }
    }
  }, [
    conversationId,
    conversationsData?.conversations,
    markFollowUpAsRead,
    navigate,
    spotlightSummaryId,
  ]);

  // Separate effect for spotlight loading - only runs when spotlightSummaryId changes
  useEffect(() => {
    if (!spotlightSummaryId) {
      return;
    }

    const loadSpotlight = async () => {
      try {
        const spotlight = await getSpotlightById(spotlightSummaryId);
        if (spotlight?.prompt) {
          useChatStore.getState().setInputPrompt(spotlight.prompt);
        }
      } catch (error) {
        console.error('Error loading spotlight:', error);
      } finally {
        setIsLoadingSpotlight(false);
      }
    };

    loadSpotlight();
  }, [spotlightSummaryId, getSpotlightById]);

  // Show loading state while spotlight is being fetched
  if (isLoadingSpotlight) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <LoadingSpinner text={t('loadingSpotlight')} />
      </div>
    );
  }

  // Render layout based on focused view state
  if (isFocusedViewOpen) {
    // Focused view mode: large left panel for content, smaller right panel for chat
    return (
      <>
        {/* Focused view panel - takes most space on the left */}
        <div
          className="sticky top-0 z-[100] h-full"
          style={{ boxShadow: '4px 0 8px -2px rgba(0, 0, 0, 0.1)' }}
        >
          <FocusedViewPanel />
        </div>

        {/* Chat area - smaller, on the right */}
        <div className="min-w-0 flex-1 bg-gray-100 dark:bg-gray-800">
          <ChatArea />
        </div>

        {/* Tool execution panel - still available but secondary */}
        <div className="sticky top-0 z-[99] h-full">
          <ToolExecutionPanel />
        </div>
      </>
    );
  }

  // Normal mode: chat takes most space, tool panel on the right
  return (
    <>
      <div className="h-full min-w-0 flex-1">
        <ChatArea isCompact={isMiniMode} />
      </div>

      {/* Tool execution panel - fixed width by default, can expand to fullscreen */}
      {!isMiniMode && (
        <div
          className="sticky top-0 z-[100] h-full"
          data-testid="tool-execution-panel"
        >
          <ToolExecutionPanel />
        </div>
      )}
    </>
  );
}
